import { Controller } from "@hotwired/stimulus"

// Manages classification category rows and the Edit Prompt Gmail label importer.
// Imported labels remain ordinary nested-form rows and are not persisted until
// the surrounding prompt form is successfully submitted.
export default class extends Controller {
  static targets = [
    "list",
    "template",
    "categoryCount",
    "source",
    "loadButton",
    "picker",
    "labelOptions",
    "selectAllButton",
    "importButton",
    "status",
    "selectionStatus",
    "reconnectLink"
  ]

  connect() {
    this.loadedLabels = []
    this.nextRowIndex = 0
    this.refreshControls()
  }

  add(event) {
    event.preventDefault()

    const row = this.buildCategoryRow()
    row.querySelector('[data-category-field="position"]').value = this.nextMatchPosition()
    this.insertBeforeFallback(row)
    this.refreshControls()
    row.querySelector('[data-category-field="key"]')?.focus()
  }

  remove(event) {
    event.preventDefault()
    const row = event.target.closest("[data-category-row]")
    if (!row) return

    const destroyField = row.querySelector('[data-category-field="destroy"]')
    if (destroyField) {
      // Persisted record: mark for deletion and hide it.
      destroyField.value = "1"
      row.classList.add("hidden")
    } else {
      // Unsaved record: just drop it from the DOM.
      row.remove()
    }

    this.refreshControls()
  }

  sourceChanged() {
    this.loadedLabels = []
    this.clearLabelPicker()
    this.setStatus("")
    this.refreshLoadButton()
  }

  async loadLabels(event) {
    event.preventDefault()

    const selectedOption = this.sourceTarget.selectedOptions[0]
    const url = selectedOption?.dataset.labelsUrl
    if (!url) {
      this.setStatus("Choose a Gmail account first.", "error")
      return
    }

    this.setLoading(true)
    this.clearLabelPicker()
    this.setStatus("Loading custom Gmail labels…")

    try {
      const response = await fetch(url, {
        credentials: "same-origin",
        headers: { Accept: "application/json" }
      })
      const payload = await response.json().catch(() => ({}))

      if (!response.ok) {
        const error = new Error(payload.error || "Gmail labels could not be loaded. Please try again.")
        error.status = response.status
        throw error
      }

      if (!Array.isArray(payload.labels)) {
        throw new Error("Gmail labels could not be loaded. Please try again.")
      }

      this.loadedLabels = payload.labels.filter((label) => (
        label && typeof label.name === "string" && typeof label.suggested_key === "string"
      ))
      this.renderLabelOptions()

      if (this.loadedLabels.length === 0) {
        this.setStatus("This account has no custom Gmail labels to import.")
      } else {
        this.setStatus(`Loaded ${this.loadedLabels.length} custom Gmail ${this.pluralize("label", this.loadedLabels.length)}.`)
      }
    } catch (error) {
      this.loadedLabels = []
      this.clearLabelPicker()
      this.setStatus(error.message || "Gmail labels could not be loaded. Please try again.", "error", error.status === 422)
    } finally {
      this.setLoading(false)
    }
  }

  selectionChanged() {
    this.updateSelectionState()
  }

  selectAll(event) {
    event.preventDefault()

    const inputs = this.importableLabelInputs()
    const shouldSelect = inputs.some((input) => !input.checked)
    inputs.forEach((input) => { input.checked = shouldSelect })
    this.updateSelectionState()
  }

  categoryChanged(event) {
    const labelField = event?.target
    const row = labelField?.closest("[data-category-row]")
    const keyField = row?.querySelector('[data-category-field="key"]')

    if (labelField?.dataset.categoryField === "label" && keyField) {
      const hiddenDraftKey = keyField.type === "hidden" && row.dataset.categoryPersisted === "false"

      if (hiddenDraftKey) keyField.value = ""
      if (hiddenDraftKey || keyField.value.trim() === "") {
        keyField.value = this.uniqueKey(this.suggestedKey(labelField.value))
      }
    }

    this.updateSelectionState()
  }

  importSelected(event) {
    event.preventDefault()

    const selected = this.selectedLabelInputs()
    if (selected.length === 0) return

    let firstCriteriaField = null

    selected.forEach((input) => {
      const row = this.buildCategoryRow()
      const key = this.uniqueKey(input.dataset.suggestedKey)

      row.querySelector('[data-category-field="key"]').value = key
      row.querySelector('[data-category-field="label"]').value = input.dataset.labelName
      row.querySelector('[data-category-field="criteria"]').value = input.dataset.labelName
      row.querySelector('[data-category-field="position"]').value = this.nextMatchPosition()

      this.insertBeforeFallback(row)
      firstCriteriaField ||= row.querySelector('[data-category-field="criteria"]')
    })

    this.renderLabelOptions()
    this.refreshControls()
    this.setStatus(
      `Added ${selected.length} ${this.pluralize("label", selected.length)} to this draft. Not saved yet — review them, then click Update Prompt.`,
      "success"
    )
    firstCriteriaField?.focus()
  }

  buildCategoryRow() {
    // Rails strong parameters recognize nested-attributes hashes by numeric keys.
    const index = (Date.now() + this.nextRowIndex++).toString()
    const html = this.templateTarget.innerHTML.replace(/NEW_RECORD/g, index)
    const fragment = document.createRange().createContextualFragment(html.trim())
    return fragment.querySelector("[data-category-row]")
  }

  insertBeforeFallback(row) {
    const fallback = Array.from(this.listTarget.querySelectorAll("[data-category-row]"))
      .find((candidate) => candidate.dataset.categoryFallback === "true" && this.rowIsActive(candidate))

    this.listTarget.insertBefore(row, fallback || null)
  }

  renderLabelOptions() {
    if (!this.hasLabelOptionsTarget || !this.hasPickerTarget) return

    this.labelOptionsTarget.replaceChildren()

    this.loadedLabels.forEach((label, index) => {
      const option = document.createElement("label")
      option.className = "flex items-start gap-2 p-2 rounded border border-sortie-border bg-sortie-hover/50 text-sm"
      option.dataset.labelOption = "true"

      const checkbox = document.createElement("input")
      checkbox.type = "checkbox"
      checkbox.id = `gmail-label-option-${index}`
      checkbox.dataset.labelName = label.name
      checkbox.dataset.suggestedKey = label.suggested_key
      checkbox.className = "mt-0.5 w-4 h-4 rounded border-sortie-border bg-sortie-bg text-purple-500 focus:ring-purple-500/50"
      checkbox.addEventListener("change", () => this.selectionChanged())

      const text = document.createElement("span")
      text.className = "min-w-0 break-words text-zinc-300"
      text.textContent = label.name

      const state = document.createElement("span")
      state.dataset.labelState = "true"
      state.className = "ml-auto flex-shrink-0 text-xs text-zinc-500"

      option.append(checkbox, text, state)
      this.labelOptionsTarget.append(option)
    })

    this.pickerTarget.classList.toggle("hidden", this.loadedLabels.length === 0)
    this.updateSelectionState()
  }

  clearLabelPicker() {
    if (this.hasLabelOptionsTarget) this.labelOptionsTarget.replaceChildren()
    if (this.hasPickerTarget) this.pickerTarget.classList.add("hidden")
    if (this.hasSelectAllButtonTarget) {
      this.selectAllButtonTarget.textContent = "Select all"
      this.selectAllButtonTarget.disabled = true
      this.selectAllButtonTarget.setAttribute("aria-pressed", "false")
    }
    if (this.hasImportButtonTarget) this.importButtonTarget.disabled = true
    if (this.hasSelectionStatusTarget) this.selectionStatusTarget.textContent = ""
  }

  updateSelectionState() {
    if (!this.hasLabelOptionsTarget) return

    const existingLabels = this.currentLabelNames()
    const inputs = this.labelInputs()

    inputs.forEach((input) => {
      const duplicate = existingLabels.has(this.normalizedLabelName(input.dataset.labelName))
      input.dataset.duplicate = duplicate ? "true" : "false"
      if (duplicate) input.checked = false
    })

    inputs.forEach((input) => {
      const duplicate = input.dataset.duplicate === "true"
      input.disabled = duplicate

      const option = input.closest("[data-label-option]")
      const state = option?.querySelector("[data-label-state]")
      if (state) state.textContent = duplicate ? "Already added" : ""
      option?.classList.toggle("opacity-50", duplicate)
    })

    const importableInputs = this.importableLabelInputs()
    const selectedCount = this.selectedLabelInputs().length
    const allSelected = importableInputs.length > 0 && selectedCount === importableInputs.length

    if (this.hasSelectAllButtonTarget) {
      this.selectAllButtonTarget.textContent = allSelected ? "Clear selection" : "Select all"
      this.selectAllButtonTarget.disabled = importableInputs.length === 0
      this.selectAllButtonTarget.setAttribute("aria-pressed", allSelected.toString())
    }

    if (this.hasImportButtonTarget) {
      this.importButtonTarget.disabled = selectedCount === 0
    }

    if (this.hasSelectionStatusTarget) {
      this.selectionStatusTarget.textContent = `${selectedCount} of ${importableInputs.length} available ${this.pluralize("label", importableInputs.length)} selected.`
    }
  }

  refreshControls() {
    this.updateFallbackPosition()
    const used = this.activeRows().length

    if (this.hasCategoryCountTarget) {
      this.categoryCountTarget.textContent = `${used} ${this.pluralize("category", used)}`
    }

    this.refreshLoadButton()
    this.updateSelectionState()
  }

  refreshLoadButton() {
    if (!this.hasLoadButtonTarget || !this.hasSourceTarget) return
    this.loadButtonTarget.disabled = this.sourceTarget.value === "" || this.loading
  }

  setLoading(loading) {
    this.loading = loading
    if (this.hasLoadButtonTarget) {
      this.loadButtonTarget.textContent = loading ? "Loading…" : "Load labels"
    }
    this.refreshLoadButton()
  }

  setStatus(message, type = "neutral", showReconnect = false) {
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = message
      this.statusTarget.classList.remove("text-zinc-400", "text-red-400", "text-green-400")

      const colorClass = type === "error" ? "text-red-400" : (type === "success" ? "text-green-400" : "text-zinc-400")
      this.statusTarget.classList.add(colorClass)
    }

    if (this.hasReconnectLinkTarget) {
      this.reconnectLinkTarget.classList.toggle("hidden", !showReconnect)
    }
  }

  activeRows() {
    return Array.from(this.listTarget.querySelectorAll("[data-category-row]"))
      .filter((row) => this.rowIsActive(row))
  }

  rowIsActive(row) {
    const destroyField = row.querySelector('[data-category-field="destroy"]')
    return !row.classList.contains("hidden") && destroyField?.value !== "1"
  }

  currentLabelNames() {
    return new Set(
      this.activeRows()
        .map((row) => row.querySelector('[data-category-field="label"]')?.value)
        .filter(Boolean)
        .map((name) => this.normalizedLabelName(name))
    )
  }

  currentKeys() {
    return new Set(
      this.activeRows()
        .map((row) => row.querySelector('[data-category-field="key"]')?.value)
        .filter(Boolean)
    )
  }

  uniqueKey(suggestedKey) {
    const normalized = this.suggestedKey(suggestedKey)
    const keys = this.currentKeys()

    if (!keys.has(normalized)) return normalized

    let suffix = 2
    while (keys.has(`${normalized}_${suffix}`)) suffix += 1
    return `${normalized}_${suffix}`
  }

  suggestedKey(value) {
    return (value || "")
      .toLowerCase()
      .replace(/[^a-z0-9_]+/g, "_")
      .replace(/^_+|_+$/g, "") || "gmail_label"
  }

  nextMatchPosition() {
    const positions = this.activeRows()
      .filter((row) => row.dataset.categoryFallback !== "true")
      .map((row) => Number.parseInt(row.querySelector('[data-category-field="position"]')?.value, 10))
      .filter(Number.isFinite)
    const highest = positions.length > 0 ? Math.max(...positions) : -1
    return highest + 1
  }

  updateFallbackPosition() {
    const fallback = this.activeRows()
      .find((row) => row.dataset.categoryFallback === "true")
    if (!fallback) return

    fallback.querySelector('[data-category-field="position"]').value = this.nextMatchPosition()
  }

  labelInputs() {
    if (!this.hasLabelOptionsTarget) return []
    return Array.from(this.labelOptionsTarget.querySelectorAll('input[type="checkbox"]'))
  }

  selectedLabelInputs() {
    return this.labelInputs().filter((input) => input.checked && input.dataset.duplicate !== "true")
  }

  importableLabelInputs() {
    return this.labelInputs().filter((input) => input.dataset.duplicate !== "true")
  }

  normalizedLabelName(name) {
    return (name || "").trim().toLocaleLowerCase()
  }

  pluralize(word, count) {
    if (count === 1) return word
    if (word === "category") return "categories"
    return `${word}s`
  }
}
